import { getTableMetaHelper, Skeleton } from '@round/ui-kit';
import { CampaignsTableMeta } from '../../../../../Campaigns/types';
import { CellContext } from '@tanstack/react-table';
import { CampaignTableRow } from '../../types';
import { Dropdown, assets } from '@round/whitelabel-ui';
import styles from './ActionsCell.module.css';
import { usePublicReportYoutubePosts } from 'Modules/Plans/PublicReports/Posts/YoutubePosts/hooks/usePublicReportYoutubePosts';
import YoutubeReportClipboardTable, {
    mapToClipboardReportRow,
    mapToClipboardTableTotals,
} from 'Modules/Plans/CopyToClipboard/projectReport/YoutubeReportClipboardTable';
import copyComponentRenderToClipboard from 'utility/copyToClipboard/copyComponentRenderToClipboard';
import { showNotification } from 'helpers';
import { useState } from 'react';

const { Copy: CopyIcon } = assets;

const getTableMeta = getTableMetaHelper<CampaignsTableMeta>();

type Props = CellContext<CampaignTableRow, any>;

const ActionsCell = ({ table, row: { original } }: Props) => {
    const { isLoading, getIsCampaignStatsLoading } = getTableMeta(table);
    const { fetchImmediate } = usePublicReportYoutubePosts(original.id, { enabled: false });
    const [isCopyLoading, setIsCopyLoading] = useState(false);

    if (isLoading || getIsCampaignStatsLoading?.(original)) {
        return <Skeleton />;
    }

    const onCopyReport = async (campaign: CampaignTableRow) => {
        try {
            setIsCopyLoading(true);
            const result = await fetchImmediate();
            if (!result) {
                showNotification('Could not copy', 'error');
                return;
            }

            const { posts, channels } = result;
            const items = posts.map((post) => {
                const accountId = post.youtube_details?.account_id;
                const user = channels.find((c) => c.id === accountId) ?? null;
                return mapToClipboardReportRow({ post, channel: user });
            });

            // Add the totals values we can get from the campaign stats
            const totals = mapToClipboardTableTotals(campaign.stats, items);
            const success = copyComponentRenderToClipboard(
                {
                    artistName: campaign.brand?.name,
                    projectName: campaign.project?.name,
                    campaignName: campaign.name,
                    currency: campaign.currency,
                    totals,
                    items,
                    includeCost: posts.some((p) => 'cost' in p),
                },
                YoutubeReportClipboardTable
            );

            if (success) {
                showNotification('Copied!', 'info');
            } else {
                showNotification('Could not copy', 'error');
            }
        } catch {
            showNotification('Could not copy', 'error');
        } finally {
            setIsCopyLoading(false);
        }
    };

    return (
        <Dropdown>
            {({ setIsOpen }) => (
                <>
                    <Dropdown.Target />
                    <Dropdown.Menu>
                        <span className={styles.copyMenuTitle}>Copy</span>
                        <Dropdown.Item
                            icon={() => <CopyIcon className={styles.copyIcon} />}
                            onClick={() => {
                                onCopyReport(original).finally(() => {
                                    setIsOpen(false);
                                });
                            }}
                        >
                            {isCopyLoading ? 'Report loading...' : 'Report'}
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </>
            )}
        </Dropdown>
    );
};

export default ActionsCell;
